"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Grid,
  GridItem,
  type GridItemSpanValue,
} from "@boostdev/design-system-components/client";
import type { Photo } from "@/lib/immich/types";
import { PhotoThumbnail } from "./PhotoThumbnail";
import { PhotoModal } from "./PhotoModal";
import {
  PhotoModalViewer,
  type PhotoModalViewerDownloadConfig,
  type PhotoModalViewerLabels,
} from "./PhotoModalViewer";
import { ShareButton, type ShareButtonLabels } from "./ShareButton";
import { usePhotoDeepLink } from "./usePhotoDeepLink";

export interface GalleryClientLabels extends PhotoModalViewerLabels {
  readonly close: string;
  readonly liveBadge: string;
  readonly videoBadge: string;
}

export interface GalleryClientProps {
  readonly photos: readonly Photo[];
  readonly labels: GalleryClientLabels;
  readonly download?: PhotoModalViewerDownloadConfig;
  readonly share?: ShareButtonLabels;
  /** URL for the album with no photo open. Used to write/read path-based
   *  deep links, e.g. `/nl/photo-code/sh26`. */
  readonly basePath: string;
  /** If set, opens the modal at this photo on initial render. Usually
   *  derived server-side from the second path segment. */
  readonly initialPhotoId?: string;
}

function photoColumnSpan(photo: Photo): GridItemSpanValue {
  if (photo.width <= 0 || photo.height <= 0) return "one-quarter";
  const ratio = photo.width / photo.height;
  if (ratio > 3.5) return "full";
  if (ratio > 2.2) return "three-quarters";
  if (ratio > 1.5) return "half";
  return "one-quarter";
}

const NARROW_VIEWPORT_QUERY = "(max-width: 47.99rem)";

function useIsNarrowViewport(): boolean {
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(NARROW_VIEWPORT_QUERY);
    const update = () => setIsNarrow(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);
  return isNarrow;
}

export function GalleryClient({
  photos,
  labels,
  download,
  share,
  basePath,
  initialPhotoId,
}: GalleryClientProps) {
  const { openIndex, isOpen, handleOpen, handleClose, handleIndexChange } =
    usePhotoDeepLink(photos, basePath, initialPhotoId);
  const isNarrow = useIsNarrowViewport();

  const viewerLabels = useMemo<PhotoModalViewerLabels>(
    () => ({
      previous: labels.previous,
      next: labels.next,
      imageFailed: labels.imageFailed,
      liveLoop: labels.liveLoop,
      liveStopLoop: labels.liveStopLoop,
    }),
    [
      labels.previous,
      labels.next,
      labels.imageFailed,
      labels.liveLoop,
      labels.liveStopLoop,
    ],
  );

  return (
    <>
      <Grid isMasonry={!isNarrow} variant="main">
        {photos.map((photo, index) => (
          <GridItem
            key={photo.id}
            columnSpan={isNarrow ? "full" : photoColumnSpan(photo)}
          >
            <PhotoThumbnail
              photo={photo}
              index={index}
              onClick={handleOpen}
              liveBadgeLabel={labels.liveBadge}
              videoBadgeLabel={labels.videoBadge}
            />
          </GridItem>
        ))}
      </Grid>
      <PhotoModal
        isOpen={isOpen}
        onClose={handleClose}
        ariaLabel={labels.close}
      >
        {openIndex !== null ? (
          <PhotoModalViewer
            photos={photos}
            index={openIndex}
            onIndexChange={handleIndexChange}
            labels={viewerLabels}
            download={download}
            toolbar={share ? <ShareButton labels={share} /> : null}
          />
        ) : null}
      </PhotoModal>
    </>
  );
}
